import {
  DINING_CHAIR_DEPTH_M,
  DINING_CHAIR_WIDTH_M,
  DINING_RECT_TABLE_LAYOUTS,
  DINING_ROUND_CHAIR_COUNT,
  diningRectChairXOffsetM,
  diningRectSymbolLengthM,
  diningRoundChairOrbitRadiusM,
  diningRoundTableRadiusM,
  type DiningRectLayoutKey,
} from "./diningLayoutConstants";

export type DiningRectLayout = (typeof DINING_RECT_TABLE_LAYOUTS)[DiningRectLayoutKey];

export function getDiningRectLayoutForChairCount(
  chairCount: number,
): DiningRectLayout & { layoutKey: DiningRectLayoutKey } {
  if (chairCount === 2) {
    return { layoutKey: "2-chair", ...DINING_RECT_TABLE_LAYOUTS["2-chair"] };
  }
  if (chairCount === 4) {
    return { layoutKey: "4-chair", ...DINING_RECT_TABLE_LAYOUTS["4-chair"] };
  }
  return { layoutKey: "6-chair", ...DINING_RECT_TABLE_LAYOUTS["6-chair"] };
}

/** Vertical footprint for rectangular dining groups (metres). */
export function diningRectContentHeightM(
  tableDepthM: number,
  chairRows: number,
): number {
  if (chairRows === 1) {
    return Math.max(tableDepthM, DINING_CHAIR_WIDTH_M);
  }
  return tableDepthM;
}

/** Chair row Y centres along the table depth (metres). */
export function diningChairRowCentersM(
  tableTopM: number,
  tableBottomM: number,
  rowCount: number,
  rowSpread = 1,
): number[] {
  if (rowCount === 1) return [(tableTopM + tableBottomM) / 2];
  const inset = DINING_CHAIR_WIDTH_M / 2;
  const start = tableTopM + inset;
  const end = tableBottomM - inset;
  const center = (start + end) / 2;
  const halfSpan = ((end - start) / 2) * rowSpread;
  if (rowCount === 2) {
    return [center - halfSpan, center + halfSpan];
  }
  const step = (2 * halfSpan) / (rowCount - 1);
  return Array.from({ length: rowCount }, (_, index) => center - halfSpan + index * step);
}

export type DiningFootprintScales = {
  scaleX: number;
  scaleY: number;
};

export function diningRectFootprintScales(
  chairCount: number,
  footprintWidthM: number,
  footprintDepthM: number,
): DiningFootprintScales {
  const layout = getDiningRectLayoutForChairCount(chairCount);
  const nominalWidthM = diningRectSymbolLengthM(layout.tableWidth);
  const nominalDepthM = diningRectContentHeightM(
    layout.tableDepth,
    layout.chairRows,
  );
  return {
    scaleX: footprintWidthM / nominalWidthM,
    scaleY: footprintDepthM / nominalDepthM,
  };
}

function rotatedChairSeatCornersM(
  cx: number,
  cy: number,
  outwardAngle: number,
): [number, number][] {
  const halfDepth = DINING_CHAIR_DEPTH_M / 2;
  const halfWidth = DINING_CHAIR_WIDTH_M / 2;
  const cos = Math.cos(outwardAngle);
  const sin = Math.sin(outwardAngle);
  const rot = (lx: number, ly: number): [number, number] => [
    cx + lx * cos - ly * sin,
    cy + lx * sin + ly * cos,
  ];
  return [
    rot(-halfDepth, -halfWidth),
    rot(halfDepth, -halfWidth),
    rot(halfDepth, halfWidth),
    rot(-halfDepth, halfWidth),
  ];
}

/** Content bbox for the round table group (metres), matching the 2D generator. */
export function diningRoundContentFootprintM(): {
  width: number;
  depth: number;
} {
  const orbit = diningRoundChairOrbitRadiusM();
  const tableRadius = diningRoundTableRadiusM();
  const points: [number, number][] = [];

  for (let angle = 0; angle <= Math.PI * 2; angle += Math.PI / 32) {
    points.push(
      [Math.cos(angle) * tableRadius, Math.sin(angle) * tableRadius],
    );
  }

  for (let i = 0; i < DINING_ROUND_CHAIR_COUNT; i += 1) {
    const angle = (Math.PI * 2 * i) / DINING_ROUND_CHAIR_COUNT;
    const cx = Math.cos(angle) * orbit;
    const cy = Math.sin(angle) * orbit;
    points.push(...rotatedChairSeatCornersM(cx, cy, angle));
  }

  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  return {
    width: Math.max(...xs) - Math.min(...xs),
    depth: Math.max(...ys) - Math.min(...ys),
  };
}

export function diningRoundFootprintScales(
  footprintWidthM: number,
  footprintDepthM: number,
): DiningFootprintScales {
  const nominal = diningRoundContentFootprintM();
  return {
    scaleX: footprintWidthM / nominal.width,
    scaleY: footprintDepthM / nominal.depth,
  };
}

export function diningRoundChairScalesAtAngle(
  angle: number,
  scales: DiningFootprintScales,
): { chairWidth: number; chairDepth: number } {
  const radialScale = Math.hypot(
    scales.scaleX * Math.cos(angle),
    scales.scaleY * Math.sin(angle),
  );
  const tangentialScale = Math.hypot(
    scales.scaleX * Math.sin(angle),
    scales.scaleY * Math.cos(angle),
  );
  return {
    chairWidth: DINING_CHAIR_WIDTH_M * tangentialScale,
    chairDepth: DINING_CHAIR_DEPTH_M * radialScale,
  };
}

export function diningRoundChairPositionAtAngle(
  angle: number,
  scales: DiningFootprintScales,
): [number, number] {
  const orbitX = diningRoundChairOrbitRadiusM() * scales.scaleX;
  const orbitY = diningRoundChairOrbitRadiusM() * scales.scaleY;
  return [Math.cos(angle) * orbitX, Math.sin(angle) * orbitY];
}
